import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Select, Switch, FormInstance, Typography, Tooltip } from "antd"
import { DeleteOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons"

import type { EntityField, Config } from "../../../coreTypes/config"
import { ScrollableBlock, Spaces, Colors, StyleHelpers } from "../../global"
import { getTypeIcon } from "../../Designer/DataField"

const { Option } = Select
const { Title } = Typography

function ProcessFieldInput(props: {
    projectConfig: Config
    field: EntityField
    parentPath?: string
    ind?: number
    deleteLastAdditionalItem?: () => void
    form?: FormInstance
    setMutationFormValues?: (input: any) => void
}): JSX.Element {
    const { projectConfig, field, parentPath, ind, deleteLastAdditionalItem, form, setMutationFormValues } = props
    let fullFieldName = parentPath ? parentPath + "." + field.name : field.name + (field.isArray ? (ind ? `.${ind}` : ".0") : "")
    // console.log(fullFieldName)

    const deleteButton = deleteLastAdditionalItem ? <Button type="text" danger onClick={deleteLastAdditionalItem} icon={<DeleteOutlined />} /> : null

    let formItem: JSX.Element = <></>
    switch (field.type) {
        case "STRING":
        case "USER_ID":
        case "OBJECT_ID":
        case "EMAIL":
        case "PHONENUMBER":
            formItem = (
                <>
                    <FormItem
                        label={
                            <FormItemLabel>
                                {getTypeIcon(field.type)}
                                {field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}
                            </FormItemLabel>
                        }
                        name={fullFieldName}
                        key={fullFieldName}
                        tooltip={field.description}
                        rules={[
                            {
                                required: field.mandatory,
                            },
                        ]}
                        // FIXME: validator for USER_ID OBJECT_ID EMAIL PHONENUMBER [Tasks]
                        // {
                        //     validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error("Should accept agreement"))),
                        // },
                    >
                        <Input placeholder={field.defaultValue ? "default: " + field.defaultValue : ""} />
                    </FormItem>
                    {deleteButton && <Form.Item label=" ">{deleteButton}</Form.Item>}
                </>
            )
            break
        case "FILE":
            // const selectedFile = form?.getFieldValue(fullFieldName + "_FILE")
            if (fullFieldName.includes(".")) {
                fullFieldName = [fullFieldName.split(".")[0] + "_FILE", fullFieldName.split(".")[1]].join(".")
            } else {
                fullFieldName = fullFieldName + "_FILE"
            }

            const labelId = `file-input-${fullFieldName}${field.isArray ? (ind ? `-${ind}` : "-0") : ""}`
            formItem = (
                <>
                    <FormItem
                        // hidden item for form object value to exist
                        label=""
                        style={{ height: 0, padding: 0, margin: 0, width: 0 }}
                        name={fullFieldName}
                        key={fullFieldName}
                        tooltip={field.description}
                    />
                    <FileFormItemWrapper>
                        <FormItemLabel>
                            {getTypeIcon(field.type)}
                            {field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}
                        </FormItemLabel>
                        {/* TODO: make file input look nicer */}
                        <FileUpload>
                            <label htmlFor={labelId} className="custom-file-upload">
                                {form?.getFieldValue(fullFieldName)?.name ?? "Choose File..."}
                            </label>
                            <input
                                id={labelId}
                                type="file"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    form?.setFieldValue(fullFieldName, e.target?.files?.[0])
                                    if (setMutationFormValues) {
                                        const mutationInput: { [key: string]: any } = prepareInput(form?.getFieldsValue())
                                        // console.log("Mutation input 3: ", mutationInput)
                                        setMutationFormValues(mutationInput)
                                    }
                                }}
                            />
                        </FileUpload>

                        {deleteButton && <Form.Item label=" ">{deleteButton}</Form.Item>}
                    </FileFormItemWrapper>
                </>
            )
            break
        case "INT":
        case "FLOAT":
        case "DOUBLE":
            formItem = (
                <>
                    <FormItem
                        label={
                            <FormItemLabel>
                                {getTypeIcon(field.type)}
                                {field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}
                            </FormItemLabel>
                        }
                        name={fullFieldName}
                        key={fullFieldName}
                        tooltip={field.description}
                        rules={[
                            {
                                required: field.mandatory,
                            },
                        ]}
                    >
                        <InputNumber
                            placeholder={field.defaultValue ? "default: " + field.defaultValue : ""}
                            // FIXME: apply Int, Float, Double formatting
                            // form.getFieldValue -> form.setFieldValue
                        />
                    </FormItem>
                    {deleteButton && <Form.Item label=" ">{deleteButton}</Form.Item>}
                </>
            )
            break
        case "BOOLEAN":
            formItem = (
                <>
                    <FormItem
                        label={
                            <FormItemLabel>
                                {getTypeIcon(field.type)}
                                {field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}
                            </FormItemLabel>
                        }
                        valuePropName="checked"
                        name={fullFieldName}
                        key={fullFieldName}
                        tooltip={field.description}
                        rules={[
                            {
                                required: field.mandatory,
                            },
                        ]}
                    >
                        <Checkbox>{field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}</Checkbox>
                    </FormItem>
                    {deleteButton && <Form.Item label=" ">{deleteButton}</Form.Item>}
                </>
            )
            break
        case "DATE":
        case "TIME":
            formItem = (
                <>
                    <FormItem
                        label={
                            <FormItemLabel>
                                {getTypeIcon(field.type)}
                                {field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}
                            </FormItemLabel>
                        }
                        name={fullFieldName}
                        key={fullFieldName}
                        tooltip={field.description}
                        rules={[
                            {
                                required: field.mandatory,
                            },
                        ]}
                    >
                        <DatePicker showTime={field.type === "TIME"} placeholder={field.defaultValue ? "default: " + field.defaultValue : ""} />
                    </FormItem>
                    {deleteButton && <Form.Item label=" ">{deleteButton}</Form.Item>}
                </>
            )
            break
        case "ENUM":
            formItem = (
                <>
                    <FormItem
                        label={
                            <FormItemLabel>
                                {getTypeIcon(field.type)}
                                {field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}
                            </FormItemLabel>
                        }
                        name={fullFieldName}
                        key={fullFieldName}
                        tooltip={field.description}
                        rules={[
                            {
                                required: field.mandatory,
                            },
                        ]}
                    >
                        <Select allowClear placeholder={field.defaultValue ? "default: " + field.defaultValue : ""}>
                            {projectConfig.enums![field.enumName!].map((val) => (
                                <Option value={val} key={val}>
                                    {val}
                                </Option>
                            ))}
                        </Select>
                    </FormItem>
                    {deleteButton && <Form.Item label=" ">{deleteButton}</Form.Item>}
                </>
            )
            break
        case "SUBOBJECT":
            const [showSubObject, setShowSubobject] = useState(false)

            formItem = (
                <SubObjectFormItemWrapper key={fullFieldName}>
                    {!field.mandatory ? (
                        <>
                            <FormItem
                                label={
                                    <FormItemLabel>
                                        {getTypeIcon(field.type)}
                                        {field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}
                                    </FormItemLabel>
                                }
                                name={fullFieldName + "_SUBOBJECT_SWITCH"}
                                key={fullFieldName + "_SUBOBJECT_SWITCH"}
                                tooltip={field.description}
                                valuePropName="checked"
                            >
                                <Switch onChange={(e: boolean) => setShowSubobject(e)} />
                            </FormItem>
                            {deleteButton && <Form.Item label=" ">{deleteButton}</Form.Item>}
                        </>
                    ) : (
                        <FormItemLabel>
                            {getTypeIcon(field.type)}
                            {field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}
                            {deleteButton}
                        </FormItemLabel>
                    )}

                    {(field.mandatory || showSubObject) && (
                        <SubObjectFieldWrapper>
                            {
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                projectConfig.subObjects[field.subObjectName].fields.map((subField) => {
                                    return (
                                        <ProcessFieldInput
                                            form={form}
                                            projectConfig={projectConfig}
                                            field={subField}
                                            parentPath={fullFieldName}
                                            setMutationFormValues={setMutationFormValues}
                                        />
                                    )
                                })
                            }
                        </SubObjectFieldWrapper>
                    )}
                </SubObjectFormItemWrapper>
            )
            break
        case "CONNECTION_BELONGSTO":
        case "CONNECTION_CONTAINS":
            if (field.connectionUnderlyingField) {
                // no need to add field name
                formItem = (
                    <ProcessFieldInput
                        form={form}
                        projectConfig={projectConfig}
                        field={field.connectionUnderlyingField}
                        parentPath={parentPath}
                        setMutationFormValues={setMutationFormValues}
                    />
                )
            }
            break
        case "CUSTOMENDPOINT":
        case "EXTERNALENDPOINT":
        case "USERSELF":
            formItem = <div key={fullFieldName}></div>
            break
        default:
            formItem = (
                <div key={fullFieldName} style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                    <FormItemLabel>
                        {field.type}
                        {field.name + (field.isArray ? (ind ? ` : ${ind}` : " : 0") : "")}
                        {deleteButton}
                    </FormItemLabel>
                    {"NOT SUPPORTED YET"}
                </div>
            )
            break
    }

    if (ind || !field.isArray) return formItem

    const [additionalItemsInd, setAdditionalItemsInd] = useState([] as number[])

    return (
        <>
            {formItem}

            {additionalItemsInd.map((ai) => (
                <ProcessFieldInput
                    key={fullFieldName + `-X-${ai}`}
                    projectConfig={projectConfig}
                    field={field}
                    parentPath={parentPath}
                    ind={additionalItemsInd.indexOf(ai) + 1}
                    form={form}
                    setMutationFormValues={setMutationFormValues}
                    deleteLastAdditionalItem={
                        additionalItemsInd.indexOf(ai) == -1
                            ? undefined
                            : () => {
                                  const updatedItems = [...additionalItemsInd]
                                  updatedItems.splice(additionalItemsInd.indexOf(ai), 1)
                                  setAdditionalItemsInd(updatedItems)
                              }
                    }
                />
            ))}

            <Form.Item label=" ">
                <Button
                    type="dashed"
                    onClick={() =>
                        setAdditionalItemsInd(
                            additionalItemsInd.length > 0 ? additionalItemsInd.concat([additionalItemsInd[additionalItemsInd.length - 1] + 1]) : [1]
                        )
                    }
                    icon={<PlusOutlined />}
                >
                    Add {field.name}
                </Button>
            </Form.Item>
        </>
    )
}

function processSubObject(values: any, subObjectKey: string) {
    const subObjectValues: { [key: string]: any } = {}
    const subObjectFields = Object.keys(values).filter((key) => key.startsWith(subObjectKey + "."))
    for (const subObjectField of subObjectFields) {
        const subObjectFieldWithoutPrefix = subObjectField.replace(subObjectKey + ".", "")
        if (
            !subObjectFieldWithoutPrefix.includes(".") &&
            subObjectFieldWithoutPrefix.includes("_SUBOBJECT_SWITCH") &&
            values[subObjectKey + "." + subObjectFieldWithoutPrefix] === true
        ) {
            subObjectValues[subObjectFieldWithoutPrefix] = processSubObject(values, subObjectKey + "." + subObjectFieldWithoutPrefix)
        } else if (!subObjectFieldWithoutPrefix.includes(".") && !subObjectFieldWithoutPrefix.includes("_SUBOBJECT_SWITCH")) {
            subObjectValues[subObjectFieldWithoutPrefix] =
                typeof values[subObjectField] === "object" && values[subObjectField] !== null
                    ? values[subObjectField].toISOString()
                    : values[subObjectField]
        }
    }

    return subObjectValues
}

const prepareInput = (values: any) => {
    // console.log(JSON.stringify(values))
    const mutationInput: { [key: string]: any } = {}

    const topValuesKeys = Object.keys(values).filter((key) => !key.includes("_SUBOBJECT_SWITCH") && !key.includes("."))
    for (const topKey of topValuesKeys) {
        // if (values[topKey]) {
        //     console.log({ topKey, value: values[topKey] })
        //     console.log(typeof values[topKey] == "object")
        //     console.log(values[topKey] instanceof Date)
        // }
        mutationInput[topKey] = typeof values[topKey] == "object" && values[topKey] instanceof Date ? values[topKey].toISOString() : values[topKey]
        // if (values[topKey]) console.log(mutationInput[topKey])
    }

    const arrays = Object.keys(values).filter((key) => key.includes(".0"))
    // console.log(arrays)
    for (const arr of arrays) {
        const resultingArr = []
        const keyName = arr.split(".0")[0]
        // console.log(keyName)
        const arrItems = Object.keys(values).filter((key) => key.startsWith(keyName))
        // console.log(arrItems)
        for (let ind = 0; true; ind++) {
            const selectedItemFields = arrItems.filter((key) => key.startsWith(keyName + `.${ind}`))
            // console.log(selectedItemFields)
            if (
                ((selectedItemFields.length == 1 || values[selectedItemFields[0]] == false) && selectedItemFields[0].endsWith("_SUBOBJECT_SWITCH")) ||
                selectedItemFields.length == 0
            )
                break

            const subValues: any = {}
            for (const sif of selectedItemFields) {
                const fieldName = sif.split(keyName + `.${ind}.`)[1]
                // console.log(fieldName)
                // console.log(sif)
                // console.log(values[sif])
                if (fieldName) {
                    subValues[fieldName] = values[sif]
                    if (Object.values(subValues).filter((sv) => sv !== undefined).length > 0) {
                        if (
                            sif.split(".")[1] &&
                            parseInt(sif.split(".")[1]).toString() === sif.split(".")[1] &&
                            resultingArr[parseInt(sif.split(".")[1])]
                        ) {
                            resultingArr[parseInt(sif.split(".")[1])] = prepareInput(subValues)
                        } else {
                            resultingArr.push(prepareInput(subValues))
                        }
                    }
                } else if (values[sif]) {
                    resultingArr.push(values[sif])
                }
            }
        }
        if (resultingArr.length > 0) mutationInput[keyName] = resultingArr
    }

    const optionalSubObjectKeys = Object.keys(values).filter((key) => !key.includes(".") && key.includes("_SUBOBJECT_SWITCH"))
    // console.log(optionalSubObjectKeys)
    for (const subObjectKey of optionalSubObjectKeys) {
        if (!values[subObjectKey]) continue
        const subObjectKeyWithoutSuffix = subObjectKey.replace("_SUBOBJECT_SWITCH", "")
        const subObjectValues = processSubObject(values, subObjectKeyWithoutSuffix)
        if (JSON.stringify({}) !== JSON.stringify(subObjectValues)) {
            mutationInput[subObjectKeyWithoutSuffix] = subObjectValues
        }
    }

    const mandatorySubObjectKeys = Object.keys(values).filter(
        (key) =>
            key.includes(".") &&
            !optionalSubObjectKeys.includes(key.split(".")[0] + "_SUBOBJECT_SWITCH") &&
            !arrays.includes(key.split(".")[0] + ".0")
    )
    // console.log(mandatorySubObjectKeys)
    for (const subObjectKey of mandatorySubObjectKeys) {
        const cleanSubObjectKey = subObjectKey.split(".")[0]
        const subObjectValues = processSubObject(values, cleanSubObjectKey)
        if (JSON.stringify({}) !== JSON.stringify(subObjectValues)) {
            mutationInput[cleanSubObjectKey] = subObjectValues
        }
    }

    // console.log("mutationInput", mutationInput)
    return mutationInput
}

/**
 * Render universal form based on the provided array of fields
 *
 * @param {Object} props
 * @param props.setMutationFormValues is used to set and store remote values for each form field
 * @param props.submitForm is used to trigger submission from inside form
 *
 * @returns ReactNode
 */
const MutationForm = (props: {
    form: FormInstance
    isSubmitting?: boolean
    submitForm?: () => void
    setMutationFormValues?: (input: any) => void
    fields?: EntityField[]
    allowedFields?: string[]
    allowedInvite?: boolean
    projectConfig: Config
    withSubmitButton?: boolean
    encapsulated?: boolean
    noImmutable?: boolean
}) => {
    const {
        form,
        isSubmitting,
        submitForm,
        setMutationFormValues,
        fields,
        allowedFields,
        allowedInvite,
        projectConfig,
        withSubmitButton,
        encapsulated,
        noImmutable,
    } = props

    useEffect(() => {
        form.resetFields()
    }, [form])

    return (
        <Form
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            labelWrap={true}
            colon={false}
            layout="horizontal"
            style={{ maxWidth: "800px" }}
            scrollToFirstError
            form={form}
            name="signupProfile"
            onFinish={(values: any) => {
                // console.log("Received values of form: ", values)
                //
                // {
                //     acceptedTerms: undefined
                //     email: "em"
                //     firstName: "fn"
                //     lastName: "ln"
                //     phoneNumber: "pn"
                // }
                //

                if (setMutationFormValues) {
                    const mutationInput: { [key: string]: any } = prepareInput(values)
                    // console.log("Mutation input: ", mutationInput)
                    setMutationFormValues(mutationInput)
                }
                if (withSubmitButton && submitForm) {
                    submitForm()
                }
            }}
            // onFieldsChange={(changedFields, allFields) => {
            //     console.log("Received changedFields of form: ", changedFields)
            //     // [{
            //     // errors: Array []
            //     // name: Array [ "firstName" ]
            //     // originRCField: true
            //     // touched: true
            //     // validated: true
            //     // validating: false
            //     // value: "fn"
            //     // warnings: Array []
            //     // }...]
            // }}
            onValuesChange={(changedValues, allValues) => {
                // console.log("All values: ", allValues)
                // console.log("Received changedValues of form: ", changedValues)
                //
                // {
                //     acceptedTerms: undefined
                //     email: "e"
                //     firstName: "m"
                //     lastName: "l"
                //     phoneNumber: undefined
                //     text: "t"
                //     time: Object { "$L": "en", "$y": 2023, "$M": 9, … }
                // }
                //

                if (setMutationFormValues) {
                    const mutationInput: { [key: string]: any } = prepareInput(allValues)
                    // console.log("Mutation input 2: ", mutationInput)
                    setMutationFormValues(mutationInput)
                }
            }}
            validateMessages={{
                required: "Please provide ${label}",
            }}
        >
            <FormFieldsWrapper encapsulated={encapsulated}>
                {fields?.map((field) => {
                    // console.log(allowedFields)
                    // console.log(JSON.stringify(field))
                    if (
                        allowedFields &&
                        !allowedFields.includes(field.name) &&
                        (!field.connectionUnderlyingField || !allowedFields.includes(field.connectionUnderlyingField.name))
                    )
                        return null

                    if (
                        noImmutable &&
                        ((!field.connectionUnderlyingField && !field.mutable) ||
                            (field.connectionUnderlyingField && !field.connectionUnderlyingField.mutable))
                    )
                        return null

                    // console.log(JSON.stringify(field))
                    return (
                        <ProcessFieldInput
                            key={field.name}
                            projectConfig={projectConfig}
                            field={field}
                            form={form}
                            setMutationFormValues={setMutationFormValues}
                        />
                    )
                })}
                {!noImmutable && allowedInvite && (
                    <Form.Item style={{ display: "flex", justifyContent: "center" }}>
                        <Tooltip title="The logged-in user has permission to invite someone. This permission can be set in the user group.">
                            <Checkbox checked disabled style={{ display: "flex", width: "max-content" }}>
                                Invite User
                            </Checkbox>
                        </Tooltip>
                    </Form.Item>
                )}
            </FormFieldsWrapper>

            {withSubmitButton && (
                <Form.Item label=" ">
                    <Button type="primary" htmlType="submit" loading={isSubmitting}>
                        Finish sign up
                    </Button>
                </Form.Item>
            )}
        </Form>
    )
}

const SignUpProfile = (props: {
    sendSignUp: () => void
    sendProfile: (input: any) => void
    allowedFields: string[] | undefined
    isCreating: boolean
    title: string
    projectConfig: Config
    userFields: EntityField[]
}) => {
    const [form] = Form.useForm()
    const { sendSignUp, sendProfile, allowedFields, isCreating, title, projectConfig, userFields } = props

    return (
        <SignUpProfileWrapper>
            <Title level={4} style={{ marginTop: 0, marginBottom: Spaces.normal }}>
                <UserOutlined style={{ marginRight: Spaces.small }} />
                {title}
            </Title>
            <MutationForm
                form={form}
                submitForm={sendSignUp}
                setMutationFormValues={sendProfile}
                isSubmitting={isCreating}
                fields={userFields}
                allowedFields={allowedFields}
                projectConfig={projectConfig}
                withSubmitButton
                encapsulated
            />
        </SignUpProfileWrapper>
    )
}

const FileUpload = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: ${Spaces.small};
    margin-bottom: 24px;

    .custom-file-upload {
        border: 1px solid ${Colors.grayNormal};
        display: inline-block;
        padding: 5px 15px;
        cursor: pointer;
        background-color: ${Colors.grayLight};
        border-radius: 4px;
        transition: background-color 0.3s;
    }

    .custom-file-upload:hover {
        background-color: ${Colors.transparentDarkFrostyBackground};
        border: 1px solid ${Colors.grayDark};
    }

    #file-input {
        position: absolute;
        right: 0;
        opacity: 0; /* Hide the input */
        margin: 0;
        padding: 0;
        cursor: pointer;
    }
`

const SignUpProfileWrapper = styled.div`
    padding: 0 ${Spaces.normal};
`

const FileFormItemWrapper = styled.div`
    display: flex;
    gap: ${Spaces.normal};
    & > div:first-child {
        min-width: 30%;
        display: flex;
        justify-content: end;
        margin-right: ${Spaces.normal};
    }
`

const FormItemLabel = styled.div`
    display: flex;
    direction: row;
    gap: ${Spaces.small};
    max-width: 100%;
    white-space: normal;
    word-break: break-word;
    align-items: center;
`

const FormItem = styled(Form.Item)`
    .ant-form-item-control-input-content {
        display: flex;
        gap: ${Spaces.small};
    }
`

const FormFieldsWrapper = styled.div<{ encapsulated?: boolean }>`
    /* ${ScrollableBlock} */
    max-height: ${(props) => (props.encapsulated ? "285px" : "unset")};
    overflow-y: auto;
    margin-bottom: ${Spaces.medium};
`

const SubObjectFormItemWrapper = styled.div`
    & > div:first-child {
        margin-bottom: ${Spaces.medium};
    }
`

const SubObjectFieldWrapper = styled.div`
    border: 1px solid ${Colors.grayNormal};
    border-radius: ${StyleHelpers.radiusMedium};
    padding: ${Spaces.medium};
    margin-bottom: ${Spaces.medium};
    padding-bottom: 0;
`

export { MutationForm, SignUpProfile, ProcessFieldInput }
